import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  CalendarDaysIcon,
  MapPinIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import {
  Avatar,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Tooltip,
} from "@heroui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "../../utils/toast";

import { CreatePostSection } from "../../components/dashboard/createPostSection";
import { OpportunityViewModal } from "../opportunities/view/opportunityViewModal";

import {
  connectStripe,
  deleteMatchup,
  downloadMatchupListPdf,
  getAllTemplates,
  getConnectStatus,
  getEventDetail,
  getMatchup,
  inviteFighter,
  sendMessage,
  updateBoutOrder,
} from "../../services/api";

import {
  DisplayMatchup,
  EventDetailResponse,
  MatchupResponse,
  MatchupStatus,
  OpportunityCardTemplate,
} from "../../types/types";

import AddMatchupModal from "./addMatchupModal";
import MatchupCard from "./matchupCard";
import DealWizardModal from "../opportunities/create/dealWizardModal";
import EventShare from "./eventShare";
import EventShareHistory from "./eventShareHistory";

const getErrorMessage = (error: any, fallback: string): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.message) {
    return error.message;
  }

  return fallback;
};

const getStoredLoginResponse = () => {
  try {
    const userJson = localStorage.getItem("loginResponse");
    return userJson ? JSON.parse(userJson) : {};
  } catch {
    return {};
  }
};

const getSafeLocale = (locale?: string): string | undefined => {
  const cleanedLocale = locale?.replace(/['"]/g, "").trim();
  if (!cleanedLocale) return undefined;

  try {
    new Intl.DateTimeFormat(cleanedLocale);
    return cleanedLocale;
  } catch {
    return undefined;
  }
};

const formatDate = (
  dateString: string | null,
  fallbackLabel: string,
  locale?: string,
): string => {
  if (!dateString) return fallbackLabel;

  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-").map(Number);
      const date = new Date(year, month - 1, day);

      return date.toLocaleDateString(getSafeLocale(locale), {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString(getSafeLocale(locale), {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
};

const formatStatusLabel = (status?: string | null): string => {
  if (!status) return "";

  const text = status.toLowerCase().replaceAll("_", " ");
  return text.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const downloadUrl = URL.createObjectURL(blob);
  const anchorElement = document.createElement("a");

  anchorElement.href = downloadUrl;
  anchorElement.download = fileName;
  anchorElement.style.display = "none";
  document.body.appendChild(anchorElement);
  anchorElement.click();
  anchorElement.remove();

  setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
};

const sanitizeFileName = (value: string): string => {
  const cleanName = value.trim().replace(/[^a-z0-9-_]+/gi, "_");
  return cleanName.replace(/^_+|_+$/g, "");
};

const isCancelledMatchup = (matchup?: DisplayMatchup | null): boolean =>
  String(matchup?.status || "").toUpperCase() === MatchupStatus.CANCELLED;

const isActiveDeal = (deal?: DisplayMatchup["redCornerDeal"]): boolean => {
  if (!deal) return false;
  const status = String(deal.status || "").toUpperCase();

  return !["CANCELLED", "REJECTED"].includes(status);
};

const hasAnyDeal = (matchup?: DisplayMatchup | null): boolean =>
  Boolean(matchup?.redCornerDeal || matchup?.blueCornerDeal);

const hasActiveDeal = (matchup?: DisplayMatchup | null): boolean =>
  isActiveDeal(matchup?.redCornerDeal) || isActiveDeal(matchup?.blueCornerDeal);

const hasOpportunity = (matchup?: DisplayMatchup | null): boolean =>
  Boolean(matchup?.opportunityCardId);

const getEventDateValue = (
  event: EventDetailResponse | null,
  key: "start" | "end"
): string | null => {
  if (!event) return null;

  const eventData: any = event;

  if (key === "start") {
    return eventData.startDate || event.startDateTime || null;
  }

  return eventData.endDate || event.endDateTime || null;
};

const EventDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("events");

  const [event, setEvent] = useState<EventDetailResponse | null>(null);
  const [matchups, setMatchups] = useState<DisplayMatchup[]>([]);
  const [draggedMatchupIndex, setDraggedMatchupIndex] = useState<number | null>(null);
  const [dragOverMatchupIndex, setDragOverMatchupIndex] = useState<number | null>(null);
  const [isUpdatingBoutOrder, setIsUpdatingBoutOrder] = useState(false);
  const [isDownloadingMatchupsPdf, setIsDownloadingMatchupsPdf] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [showAddMatchup, setShowAddMatchup] = useState(false);
  const [editingMatchup, setEditingMatchup] = useState<
    MatchupResponse | DisplayMatchup | null
  >(null);
  const [matchupToDelete, setMatchupToDelete] = useState<DisplayMatchup | null>(null);
  const [isDeletingMatchup, setIsDeletingMatchup] = useState(false);
  const [isTemplateSelectorOpen, setIsTemplateSelectorOpen] = useState(false);
  const [templates, setTemplates] = useState<OpportunityCardTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [opportunityMatchupId, setOpportunityMatchupId] = useState<
    string | number | null
  >(null);
  const [viewOpportunityCardId, setViewOpportunityCardId] = useState<
    string | number | null
  >(null);
  const [connectStatus, setConnectStatus] = useState<string | null>(null);
  const [connectStatusLoading, setConnectStatusLoading] = useState(false);
  const [connectAccountLoading, setConnectAccountLoading] = useState(false);
  const isUpdatingBoutOrderRef = useRef(false);

  const loginResponse = getStoredLoginResponse();
  const currentUserId = loginResponse.id;
  const currentUsername =
    loginResponse.username || loginResponse.email?.split("@")[0] || "";

  useEffect(() => {
    if (!currentUserId) return;
    const loadConnectStatus = async () => {
      setConnectStatusLoading(true);

      try {
        const status = await getConnectStatus();

        if (status) {
          setConnectStatus(status.trim().toUpperCase());
        } else {
          setConnectStatus(null);
        }
      } catch {
        setConnectStatus(null);
      } finally {
        setConnectStatusLoading(false);
      }
    };

    loadConnectStatus();
  }, [currentUserId]);

  const handleConnectAccount = async () => {
    if (connectAccountLoading) return;
    setConnectAccountLoading(true);

    try {
      const url = await connectStripe();
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to open account onboarding"));
    } finally {
      setConnectAccountLoading(false);
    }
  };

  const opportunityEventDefaults = useMemo(
    () => ({
      country: event?.country || event?.location || "",
      city: event?.city || "",
      venue: event?.venue || "",
      eventDate: getEventDateValue(event, "start")?.split("T")[0] || "",
    }),
    [event],
  );

  const selectedOpportunityMatchup = useMemo(
    () =>
      matchups.find(
        (matchup) =>
          String(matchup.id || "") === String(opportunityMatchupId || ""),
      ),
    [matchups, opportunityMatchupId],
  );

  const opportunityDefaults = useMemo(() => {
    const selectedMatchup: any = selectedOpportunityMatchup;

    return {
      ...opportunityEventDefaults,
      sport: selectedMatchup?.combatSport || "",
      weightClass: selectedMatchup?.weightClass || "",
    };
  }, [opportunityEventDefaults, selectedOpportunityMatchup]);

  const isEventTooCloseForOpportunity = useMemo(() => {
    const startDateStr = getEventDateValue(event, "start");
    if (!startDateStr) return false;
    const eventStartDate = new Date(startDateStr);
    eventStartDate.setHours(0, 0, 0, 0);
    const minAllowedDate = new Date();
    minAllowedDate.setHours(0, 0, 0, 0);
    minAllowedDate.setDate(minAllowedDate.getDate() + 7);

    return eventStartDate < minAllowedDate;
  }, [event]);

  const loadEvent = async () => {
    if (!id) return;
    setLoading(true);
    setErrorMsg("");

    try {
      const data = await getEventDetail(id, currentUsername);
      setEvent(data);
      setMatchups(Array.isArray(data?.matchups) ? data.matchups : []);
    } catch (error) {
      const rawMessage = getErrorMessage(error, "");
      if (
        rawMessage.toLowerCase().includes("static resource") ||
        rawMessage.includes("404")
      ) {
        setErrorMsg(
          t("event_not_found", "Event not found or currently unavailable."),
        );
      } else {
        setErrorMsg(
          rawMessage ||
            t(
              "event_load_failed",
              "Could not load event details. Please try again.",
            ),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const canManageEvent =
    String(event?.organizer?.id || "") === String(currentUserId || "");
  const canManageMatchup = (matchup: DisplayMatchup): boolean =>
    canManageEvent && !isCancelledMatchup(matchup);
  const canEditMatchup = (matchup: DisplayMatchup): boolean =>
    canManageEvent && !isCancelledMatchup(matchup) && !hasActiveDeal(matchup);

  const canDeleteMatchup = (matchup: DisplayMatchup): boolean =>
    canManageEvent && !hasOpportunity(matchup) && !hasAnyDeal(matchup);

  useEffect(() => {
    loadEvent();
  }, [id]);

  const handleOpenAddMatchup = () => {
    setEditingMatchup(null);
    setShowAddMatchup(true);
  };

  const handleEditMatchupClick = async (matchupItem: DisplayMatchup) => {
    if (!matchupItem?.id) return;
    if (!canEditMatchup(matchupItem)) return;

    try {
      const data = await getMatchup(Number(matchupItem.id), currentUsername);
      setEditingMatchup({
        ...data,
        redCornerDeal: data.redCornerDeal ?? matchupItem.redCornerDeal,
        blueCornerDeal: data.blueCornerDeal ?? matchupItem.blueCornerDeal,
      });
      setShowAddMatchup(true);
    } catch (err) {
      console.warn(
        "Failed to fetch single matchup via API, using item data:",
        err,
      );
      setEditingMatchup(matchupItem);
      setShowAddMatchup(true);
    }
  };

  const handleDeleteMatchupClick = (matchupItem: DisplayMatchup) => {
    if (!canDeleteMatchup(matchupItem)) return;
    setMatchupToDelete(matchupItem);
  };

  const handleConfirmDeleteMatchup = async () => {
    if (!matchupToDelete?.id || isDeletingMatchup) return;
    if (!canDeleteMatchup(matchupToDelete)) {
      setMatchupToDelete(null);
      return;
    }
    setIsDeletingMatchup(true);

    try {
      await deleteMatchup(currentUsername, Number(matchupToDelete.id));
      toast.success(
        t("matchup_deleted_successfully", "Matchup deleted successfully"),
      );
      setMatchupToDelete(null);
      await loadEvent();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          t("failed_to_delete_matchup", "Failed to delete matchup"),
        ),
      );
    } finally {
      setIsDeletingMatchup(false);
    }
  };

  const resetDragState = () => {
    setDraggedMatchupIndex(null);
    setDragOverMatchupIndex(null);
  };

  const handleViewOpportunity = (matchup: DisplayMatchup) => {
    if (!matchup.opportunityCardId) return;

    setViewOpportunityCardId(matchup.opportunityCardId);
  };

  const handleContact = async (fighterId: string | number) => {
    if (!event?.id) return;

    try {
      await inviteFighter(fighterId, event.id);

      try {
        await sendMessage({
          receiverId: fighterId,
          message: `Hi, I would like to contact you regarding the event ${event.name}.`,
          chatType: "INDIVIDUAL",
          type: "CONTENT",
        });
      } catch (messageError) {
        console.warn(
          "Message failed but fighter invite was created",
          messageError,
        );
      }

      toast.success(
        t("fighter_invited_successfully", "Fighter invited successfully"),
      );

      await loadEvent();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          t("failed_to_invite_fighter", "Failed to invite fighter"),
        ),
      );
    }
  };

  const handleCreateOpportunity = async (matchupId?: string | number | null) => {
    setOpportunityMatchupId(matchupId ?? null);
    setTemplatesLoading(true);
    setIsTemplateSelectorOpen(true);

    try {
      const data = await getAllTemplates();
      setTemplates(data || []);
    } catch {
      toast.error(t("failed_to_load_templates", "Failed to load templates"));
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const closeAddMatchupModal = () => {
    setShowAddMatchup(false);
    setEditingMatchup(null);
  };

  const closeTemplateSelector = () => {
    setIsTemplateSelectorOpen(false);
    setOpportunityMatchupId(null);
  };

  const handleOpportunityCreated = async () => {
    closeTemplateSelector();
    await loadEvent();
  };

  const closeOpportunityView = () => {
    setViewOpportunityCardId(null);
  };

  const handleMatchupDragStart = (
    index: number,
    dragEvent: React.DragEvent<HTMLDivElement>,
  ) => {
    const draggedMatchup = matchups[index];
    if (
      !canManageMatchup(draggedMatchup) ||
      isUpdatingBoutOrder ||
      isUpdatingBoutOrderRef.current
    ) {
      dragEvent.preventDefault();
      return;
    }
    setDraggedMatchupIndex(index);
    setDragOverMatchupIndex(index);
    dragEvent.dataTransfer.effectAllowed = "move";
    dragEvent.dataTransfer.setData("text/plain", String(index));
  };

  const handleMatchupDragOver = (
    dragEvent: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    if (
      !canManageEvent ||
      isUpdatingBoutOrder ||
      isUpdatingBoutOrderRef.current
    ) {
      return;
    }

    dragEvent.preventDefault();
    dragEvent.dataTransfer.dropEffect = "move";
    if (draggedMatchupIndex !== null) {
      setDragOverMatchupIndex(index);
    }
  };

  const handleMatchupDrop = async (
    dragEvent: React.DragEvent<HTMLDivElement>,
    targetIndex: number,
  ) => {
    dragEvent.preventDefault();
    if (
      !canManageEvent ||
      draggedMatchupIndex === null ||
      draggedMatchupIndex === targetIndex ||
      isUpdatingBoutOrder ||
      isUpdatingBoutOrderRef.current
    ) {
      resetDragState();
      return;
    }
    const previousMatchups = [...matchups];
    const reorderedMatchups = [...matchups];

    const [movedMatchup] = reorderedMatchups.splice(
      draggedMatchupIndex,
      1,
    );
    if (!movedMatchup?.id) {
      resetDragState();
      return;
    }
    if (isCancelledMatchup(movedMatchup)) {
      resetDragState();
      return;
    }

    reorderedMatchups.splice(targetIndex, 0, movedMatchup);
    const activeReorderedMatchups = reorderedMatchups.filter(
      (matchup) => !isCancelledMatchup(matchup),
    );
    const newBoutOrder =
      activeReorderedMatchups.findIndex(
        (matchup) => String(matchup.id) === String(movedMatchup.id),
      ) + 1;
    const oldBoutOrder =
      previousMatchups
        .filter((matchup) => !isCancelledMatchup(matchup))
        .findIndex((matchup) => String(matchup.id) === String(movedMatchup.id)) +
      1;
    if (!newBoutOrder || newBoutOrder === oldBoutOrder) {
      resetDragState();
      return;
    }

    let nextActiveBoutOrder = 1;
    const updatedMatchups = reorderedMatchups.map((matchup) => {
      if (isCancelledMatchup(matchup)) {
        return matchup;
      }
      const updatedMatchup = {
        ...matchup,
        boutOrder: nextActiveBoutOrder,
      };

      nextActiveBoutOrder += 1;

      return updatedMatchup;
    });
    setMatchups(updatedMatchups);
    resetDragState();
    isUpdatingBoutOrderRef.current = true;
    setIsUpdatingBoutOrder(true);

    try {
      await updateBoutOrder(currentUsername, String(movedMatchup.id), {
        boutOrder: newBoutOrder,
      });
      toast.success(
        t("bout_order_updated", "Bout order updated successfully"),
      );
      await loadEvent();
    } catch (error) {
      setMatchups(previousMatchups);
      toast.error(
        getErrorMessage(
          error,
          t("failed_to_update_bout_order", "Failed to update bout order"),
        ),
      );
    } finally {
      isUpdatingBoutOrderRef.current = false;
      setIsUpdatingBoutOrder(false);
    }
  };

  const handleMatchupDragEnd = () => {
    resetDragState();
  };

  const handleMatchupSaved = async () => {
    setShowAddMatchup(false);
    setEditingMatchup(null);
    await loadEvent();
  };

  const handleDownloadMatchupsPdf = async () => {
    if (!event?.id || isDownloadingMatchupsPdf) return;
    setIsDownloadingMatchupsPdf(true);

    try {
      const pdfBlob = await downloadMatchupListPdf(currentUsername, event.id);
      const eventName = sanitizeFileName(event.name || "event") || "event";
      const fileName = `${eventName}_matchups.pdf`;

      downloadBlob(pdfBlob, fileName);
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          t("failed_to_download_matchups_pdf", "Failed to download matchups PDF"),
        ),
      );
    } finally {
      setIsDownloadingMatchupsPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full min-h-[90vh] flex flex-col items-center justify-center gap-3 bg-[#f8fafc]">
        <Spinner size="lg" color="primary" />
        <p className="text-xs font-medium text-slate-500">
          {t("loading_event_details", "Loading event details...")}
        </p>
      </div>
    );
  }

  if (errorMsg || !event) {
    return (
      <div className="w-full min-h-[90vh] p-6 bg-[#f8fafc]">
        <div className="max-w-md mx-auto bg-white p-6 rounded-3xl border border-slate-200 shadow-sm text-center">
          <h2 className="text-base font-bold text-slate-800 mb-2">
            {t("failed_to_load_event", "Failed to load Event")}
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            {errorMsg || t("event_not_found", "Event not found.")}
          </p>
          <Button
            color="primary"
            variant="flat"
            size="sm"
            onPress={() => navigate("/events")}
            className="rounded-xl font-medium"
          >
            {t("back_to_events", "Back to Events")}
          </Button>
        </div>
      </div>
    );
  }

  const organizerFirstName = event.organizer?.firstName || "";
  const organizerLastName = event.organizer?.lastName || "";
  const organizerName = `${organizerFirstName} ${organizerLastName}`.trim() || (event as any).organizerName || "Organizer";
  const organizerInitials =
    `${organizerFirstName.charAt(0)}${organizerLastName.charAt(0)}`.trim() || "O";

  const eventCity = event.city || "";
  const eventCountry = event.country || event.location || "";
  let eventLocationText = t("location_tbd", "Location TBD");

  if (event.venue && (eventCity || eventCountry)) {
    eventLocationText = `${event.venue}, ${[eventCity, eventCountry].filter(Boolean).join(", ")}`;
  } else if (eventCity && eventCountry) {
    eventLocationText = `${eventCity}, ${eventCountry}`;
  } else if (event.venue || eventCity || eventCountry) {
    eventLocationText = event.venue || eventCity || eventCountry;
  }

  const tbdLabel = t("tbd", "TBD");
  const eventStartDate = getEventDateValue(event, "start");
  const commission =
    (event as any).commission ||
    (event as any).sanctioningBody ||
    (event as any).organization ||
    "Sanctioning Body";

  let nextBoutOrder = 1;
  while (
    matchups.some((matchup) => Number(matchup.boutOrder) === nextBoutOrder)
  ) {
    nextBoutOrder += 1;
  }
  const addMatchupEventId = id ?? "";
  const shouldRenderAddMatchupModal = Boolean(addMatchupEventId);

  return (
    <div className="w-full min-h-[90vh] bg-[#f8fafc] overflow-y-auto">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200/80 px-4 sm:px-6 py-4 flex-shrink-0 shadow-sm">
        <div className="lg:hidden relative flex items-center justify-center min-h-[36px]">
          <button
            type="button"
            onClick={() => navigate("/events")}
            className="absolute left-0 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors shrink-0"
            aria-label={t("back_to_events", "Back to events")}
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>

          <h1 className="text-base font-bold text-slate-900 truncate max-w-[60%] text-center">
            {event.name}
          </h1>

          <div className="absolute right-0 flex items-center gap-1">
            {canManageEvent && (
              <>
                <EventShare
                  eventId={event.id}
                  eventName={event.name}
                  username={currentUsername}
                />
                <EventShareHistory
                  eventId={event.id}
                  eventName={event.name}
                  username={currentUsername}
                />
              </>
            )}
            <Button
              isIconOnly
              size="sm"
              color="primary"
              variant="flat"
              className="h-9 w-9 min-w-9 shrink-0 rounded-xl"
              isLoading={isDownloadingMatchupsPdf}
              onPress={handleDownloadMatchupsPdf}
              aria-label={t("download_matchup_list_pdf", "Download matchup list PDF")}
            >
              {!isDownloadingMatchupsPdf && (
                <ArrowDownTrayIcon className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="hidden lg:flex w-full max-w-[1200px] mx-auto flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate("/events")}
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors shrink-0"
              aria-label={t("back_to_events", "Back to events")}
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate">
              {event.name}
            </h1>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {canManageEvent && (
              <div className="flex items-center gap-1">
                <EventShare
                  eventId={event.id}
                  eventName={event.name}
                  username={currentUsername}
                />
                <EventShareHistory
                  eventId={event.id}
                  eventName={event.name}
                  username={currentUsername}
                />
              </div>
            )}

            <Tooltip
              content={t(
                "download_matchup_list_pdf_tooltip",
                "Download this event's full matchup list as a PDF",
              )}
              placement="bottom"
              closeDelay={0}
            >
              <Button
                isIconOnly
                size="sm"
                color="primary"
                variant="flat"
                className="h-9 w-9 min-w-9 shrink-0 rounded-xl"
                isLoading={isDownloadingMatchupsPdf}
                onPress={handleDownloadMatchupsPdf}
                aria-label={t(
                  "download_matchup_list_pdf",
                  "Download matchup list PDF",
                )}
              >
                {!isDownloadingMatchupsPdf && (
                  <ArrowDownTrayIcon className="h-4 w-4" />
                )}
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 py-6 space-y-6">
        <CreatePostSection eventId={id} className="p-0" />

        <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.03)] space-y-6">
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
            {event.description ||
              "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse blandit odio vel tortor lobortis, vel sagittis augue elementum. Morbi nec mattis sem. Mauris gravida, ex sit amet fermentum elementum, justo tellus tempus sem, sed sollicitudin nunc justo non mauris."}
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 pt-2">
            <div>
              <p className="text-xs text-slate-400 font-medium mb-1.5">
                {t("organizer", "Organizer")}
              </p>
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar
                  src={event.organizer?.profileImage || undefined}
                  name={organizerInitials}
                  size="sm"
                  className="bg-slate-100 text-slate-700 border border-slate-200 font-bold flex-shrink-0 w-8 h-8 rounded-full shadow-sm"
                />
                <span className="text-sm sm:text-base font-semibold text-slate-900 truncate">
                  {organizerName}
                </span>
                {event.organizer?.verified && (
                  <CheckBadgeIcon className="w-4 h-4 text-blue-500 shrink-0" />
                )}
              </div>
            </div>

            <div>
              <p className="text-xs text-slate-400 font-medium mb-1.5">
                {t("location", "Location")}
              </p>
              <div className="flex items-center gap-1.5 min-w-0">
                <MapPinIcon className="w-4 h-4 text-slate-800 shrink-0" />
                <span className="text-sm sm:text-base font-semibold text-slate-900 truncate" title={eventLocationText}>
                  {eventLocationText}
                </span>
              </div>
            </div>

            <div>
              <p className="text-xs text-slate-400 font-medium mb-1.5">
                {t("date", "Date")}
              </p>
              <div className="flex items-center gap-1.5 min-w-0">
                <CalendarDaysIcon className="w-4 h-4 text-slate-800 shrink-0" />
                <span className="text-sm sm:text-base font-semibold text-slate-900 truncate">
                  {formatDate(eventStartDate, tbdLabel, i18n.language)}
                </span>
              </div>
            </div>

            <div>
              <p className="text-xs text-slate-400 font-medium mb-1.5">
                {t("commission", "Commission")}
              </p>
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm sm:text-base font-semibold text-slate-900 truncate">
                  {commission}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                {t("fight_card_matchups", "Fight card & matchups")}
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {matchups.length}{" "}
                {matchups.length === 1
                  ? t("bout", "bout")
                  : t("bouts", "bouts")}
              </p>
            </div>

            {canManageEvent && (
              <Button
                size="sm"
                color="primary"
                startContent={<PlusIcon className="w-4 h-4 stroke-2" />}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-2xl px-4 py-2 text-xs sm:text-sm font-medium shadow-sm"
                onPress={handleOpenAddMatchup}
              >
                {t("add_matchup", "Add matchup")}
              </Button>
            )}
          </div>

          {matchups.length === 0 ? (
            <div className="min-h-[180px] flex flex-col items-center justify-center text-center border border-dashed border-slate-200 rounded-3xl bg-white p-8 shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mb-3 text-blue-600">
                <PlusIcon className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-slate-800">
                {t("no_matchups", "No matchups yet")}
              </h3>
              <p className="text-xs text-slate-400 mt-1 max-w-sm">
                {t(
                  "add_first_bout",
                  "Start building your fight card by adding the first bout.",
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {matchups.map((matchup, index) => {
                const canManageThisMatchup = canManageMatchup(matchup);
                const canEditThisMatchup = canEditMatchup(matchup);
                const canDeleteThisMatchup = canDeleteMatchup(matchup);
                const opportunityStatus = String(
                  matchup.opportunityCardStatus || ""
                ).toUpperCase();

                const canCreateDealForMatchup =
                  canManageThisMatchup && opportunityStatus === "PUBLISH";

                return (
                  <div
                    key={matchup.id || index}
                    draggable={canManageThisMatchup && !isUpdatingBoutOrder}
                    onDragStart={(dragEvent) =>
                      handleMatchupDragStart(index, dragEvent)
                    }
                    onDragOver={(dragEvent) =>
                      handleMatchupDragOver(dragEvent, index)
                    }
                    onDrop={(dragEvent) =>
                      handleMatchupDrop(dragEvent, index)
                    }
                    onDragEnd={handleMatchupDragEnd}
                    className={`rounded-3xl transition-all duration-200 ${
                      canManageThisMatchup && !isUpdatingBoutOrder
                        ? "cursor-grab active:cursor-grabbing"
                        : ""
                    } ${
                      draggedMatchupIndex === index
                        ? "scale-[0.99] opacity-50"
                        : ""
                    } ${
                      dragOverMatchupIndex === index &&
                      draggedMatchupIndex !== index
                        ? "ring-2 ring-blue-400 ring-offset-2"
                        : ""
                    }`}
                  >
                    <MatchupCard
                      matchup={matchup}
                      boutNumber={index + 1}
                      onContact={handleContact}
                      isEventOwner={canManageThisMatchup}
                      canCreateDeal={canCreateDealForMatchup}
                      connectStatus={connectStatus}
                      connectStatusLoading={
                        connectStatusLoading || connectAccountLoading
                      }
                      onConnectAccount={handleConnectAccount}
                      onViewOpportunity={handleViewOpportunity}
                      onCreateOpportunity={
                        canManageThisMatchup && !isEventTooCloseForOpportunity
                          ? () => handleCreateOpportunity(matchup.id)
                          : undefined
                      }
                      onEdit={
                        canEditThisMatchup
                          ? handleEditMatchupClick
                          : undefined
                      }
                      onDelete={
                        canDeleteThisMatchup
                          ? handleDeleteMatchupClick
                          : undefined
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {shouldRenderAddMatchupModal && (
        <AddMatchupModal
          isOpen={showAddMatchup}
          eventId={addMatchupEventId}
          nextBoutOrder={nextBoutOrder}
          editingMatchup={editingMatchup}
          onClose={closeAddMatchupModal}
          onCreated={handleMatchupSaved}
          onUpdated={handleMatchupSaved}
        />
      )}

      <DealWizardModal
        isOpen={isTemplateSelectorOpen}
        onClose={closeTemplateSelector}
        templates={templates}
        templatesLoading={templatesLoading}
        matchupId={opportunityMatchupId}
        eventDefaults={opportunityDefaults}
        onCreated={handleOpportunityCreated}
      />

      <Modal
        isOpen={Boolean(matchupToDelete)}
        onOpenChange={(open) => {
          if (!open) {
            setMatchupToDelete(null);
          }
        }}
        size="sm"
        backdrop="opaque"
        classNames={{
          backdrop: "bg-black/40",
          base: "bg-white rounded-3xl shadow-xl p-2",
          header: "px-6 pt-5 pb-2 border-b border-slate-100 text-base font-bold text-slate-900",
          body: "px-6 py-4 text-slate-600 text-sm",
          footer:
            "px-6 pb-5 pt-3 border-t border-slate-100 flex justify-end gap-2",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>
                {t("confirm_delete_matchup_title", "Delete Bout")}
              </ModalHeader>
              <ModalBody>
                <p>
                  {t(
                    "confirm_delete_matchup_message",
                    "Are you sure you want to delete this matchup? This action cannot be undone.",
                  )}
                </p>
                {matchupToDelete && (
                  <div className="mt-2 text-xs font-semibold text-slate-700 bg-slate-50 p-2.5 rounded-xl border border-slate-200 flex items-center justify-between">
                    <span>
                      {t("bout", "Bout")} #{matchupToDelete.boutOrder || 1}
                    </span>
                    <span className="text-slate-400">
                      {formatStatusLabel(
                        matchupToDelete.status || MatchupStatus.DRAFT,
                      )}
                    </span>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  variant="light"
                  size="sm"
                  onPress={() => {
                    setMatchupToDelete(null);
                    onClose();
                  }}
                  className="text-slate-600 font-medium rounded-xl"
                >
                  {t("cancel", "Cancel")}
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  isLoading={isDeletingMatchup}
                  onPress={handleConfirmDeleteMatchup}
                  className="font-medium bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-none"
                >
                  {t("delete", "Delete")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {viewOpportunityCardId && (
        <OpportunityViewModal
          isOpen={Boolean(viewOpportunityCardId)}
          onClose={closeOpportunityView}
          cardId={viewOpportunityCardId}
          activeTab="myCards"
          onRefresh={loadEvent}
        />
      )}
    </div>
  );
};

export default EventDetail;